import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, FileSearch, Languages, ShieldAlert, MessageSquare, ScanSearch } from "lucide-react";
import { TerraaChat } from "../components/TerraaChat";
import { DocumentScanner } from "../components/DocumentScanner";

const CAPABILITIES = [
  { icon: FileSearch, title: "OCR + NER document scanning", text: "Upload a sale deed, 7/12 extract, EC, or tax receipt — Terraa AI reads it, extracts owners, survey numbers, dates & amounts, and flags risks." },
  { icon: Languages, title: "Marathi & Hindi friendly", text: "Documents in regional languages are read in the language they're written in." },
  { icon: ShieldAlert, title: "Maps the plot, knows the deal-breakers", text: "See where the plot is on a map from the document itself. Missing Mother Deed? Disputed EC? It flags what needs expert verification." },
];

export default function AskTerraaPage() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "scan" ? "scan" : "chat");

  return (
    <div data-testid="ask-terraa-page">
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            <div className="lg:col-span-2">
              <p className="tt-eyebrow mb-4">Terraa AI</p>
              <h1 className="font-heading text-3xl md:text-5xl font-medium text-[#113835] tracking-tight">
                Ask anything about Indian property.
              </h1>
              <p className="mt-5 text-base text-gray-600 font-light leading-relaxed">
                Chat in plain language, or scan a property document — Terraa AI reads it, extracts every detail,
                and shows you where the plot is.
              </p>
              <div className="mt-10 space-y-7">
                {CAPABILITIES.map((c) => (
                  <div key={c.title} className="flex gap-4">
                    <c.icon size={22} strokeWidth={1.25} className="text-[#C1A74A] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-heading text-sm font-medium text-[#113835]">{c.title}</p>
                      <p className="mt-1 text-sm text-gray-600 font-light leading-relaxed">{c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10 bg-[#F9FAFA] border border-[#113835]/10 p-6">
                <p className="text-sm text-gray-600 font-light leading-relaxed">
                  Terraa AI can't access live government records. For a real, source-verified check on a specific
                  property, request an expert verification.
                </p>
                <Link to="/request-title-search" data-testid="ask-terraa-verify-cta" className="tt-btn-outline mt-4 inline-flex !py-2.5">
                  Request Property Verification <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-3 border-2 border-[#113835] shadow-[0_24px_60px_rgba(17,56,53,0.12)] bg-white h-[680px] flex flex-col">
              <div className="bg-[#113835] px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-1">
                  <button
                    data-testid="ai-tab-chat"
                    onClick={() => setTab("chat")}
                    className={`flex items-center gap-2 font-heading text-[0.66rem] uppercase tracking-[0.16em] px-4 py-2 transition-colors ${tab === "chat" ? "bg-[#F4D387] text-[#113835]" : "text-white/70 hover:text-white"}`}
                  >
                    <MessageSquare size={13} /> Chat
                  </button>
                  <button
                    data-testid="ai-tab-scan"
                    onClick={() => setTab("scan")}
                    className={`flex items-center gap-2 font-heading text-[0.66rem] uppercase tracking-[0.16em] px-4 py-2 transition-colors ${tab === "scan" ? "bg-[#F4D387] text-[#113835]" : "text-white/70 hover:text-white"}`}
                  >
                    <ScanSearch size={13} /> Scan Document
                  </button>
                </div>
                <span className="hidden sm:flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.14em] text-white/60">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online
                </span>
              </div>
              <div className="flex-1 min-h-0">
                {tab === "chat" ? <TerraaChat /> : <DocumentScanner />}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
